/**
 * Cow rigid body under gravity and aerodynamic drag.
 *
 *   F_drag = 0.5 * rho * C_d * A * |v_rel| * v_rel,   v_rel = v_wind - v_body
 *
 * Modes:
 * - spherical: smooth sphere, C_d = SPHERE_DRAG_COEFFICIENT, A = pi r^2.
 * - vacuum: rho = 0, so the cow is ballistic and the wind cannot touch it.
 * - realistic: handled by realisticCow.ts (tumbling ellipsoid), not here.
 *
 * Terminal velocity in still air (terminalVelocity.test.ts):
 *   v_t = sqrt(2 m g / (rho C_d A))
 *
 * Ground contact (y = 0 plane): the cow lands perfectly inelastically and
 * then rolls without slipping. A rolling solid sphere has
 * I = (2/5) m r^2, so a horizontal force F gives a = F / (m + I / r^2)
 * = F / ((7/5) m). No rolling resistance. Both are assumptions listed in
 * docs/MODEL_VALIDATION.md section 3.
 */
import { STANDARD_GRAVITY_MPS2 } from '../config/constants';
import type { WindField, WindSample } from './windField';

export type CowMode = 'spherical' | 'realistic' | 'vacuum';

/**
 * Smooth-sphere drag coefficient, subcritical Reynolds number.
 * Value from BUILD_SPEC.md. Published measurements of smooth spheres in the
 * subcritical range (Re below about 2.5e5) put C_d roughly between 0.42 and
 * 0.50. Loth, E., Tyler Daspit, J., Jeong, M., Nagata, T., and Nonomura, T.
 * (2021). Supersonic and Hypersonic Drag Coefficients for a Sphere. AIAA
 * Journal, 59(8), 3261-3274. doi:10.2514/1.J060153. That paper compares
 * against White and Hoerner data in the subcritical range; 0.47 sits inside it.
 * TODO(source): replace with the exact table entry from White, Fluid
 * Mechanics, or Hoerner (1965), Fluid-Dynamic Drag, once checked in print.
 */
export const SPHERE_DRAG_COEFFICIENT = 0.47;

/**
 * Mature Holstein cow mass, kg.
 * Source: estimated mature body weight of Brazilian Holstein cows, 705 kg.
 * Busanello, M., Corrêa Santo, M., and de Almeida, R. (2025). Are the
 * recommended proportions of mature body weight being achieved in different
 * stages of growth in Brazilian Holstein and Jersey dairy cattle? JDS
 * Communications, 6, 522-526. doi:10.3168/jdsc.2025-0743 (PMC12848261).
 */
export const COW_MASS_KG = 705;

/**
 * Body density used to turn the cow into a sphere, kg/m^3.
 * Assumption, not a measurement: a cow is taken to be about as dense as
 * water. This is the spherical cow approximation in its purest form.
 */
export const COW_BODY_DENSITY_KG_M3 = 1000;

/** Rolling solid sphere: m_eff = m + I / r^2 = (7/5) m. */
const ROLLING_MASS_FACTOR = 7 / 5;

/** Internal substep cap. Keeps RK2 stable at high time scales. */
const MAX_INTERNAL_DT_S = 0.5;

export interface RigidBodyState {
  posX: number;
  posY: number;
  posZ: number;
  velX: number;
  velY: number;
  velZ: number;
  /** Position before the last step, for render interpolation. */
  prevX: number;
  prevY: number;
  prevZ: number;
  onGround: boolean;
}

export interface CowBodyParams {
  mode: CowMode;
  massKg: number;
  /** Radius of the equivalent sphere, m. */
  radiusM: number;
  airDensity: number;
}

/** Radius of a sphere with the given mass and density, m. */
export function equivalentSphereRadius(massKg: number, densityKgM3: number): number {
  return Math.cbrt((3 * massKg) / (4 * Math.PI * densityKgM3));
}

/** Terminal speed in still air, m/s. Infinite in vacuum. */
export function terminalVelocity(p: CowBodyParams): number {
  const rho = effectiveDensity(p);
  if (rho === 0) return Number.POSITIVE_INFINITY;
  const area = Math.PI * p.radiusM * p.radiusM;
  return Math.sqrt((2 * p.massKg * STANDARD_GRAVITY_MPS2) / (rho * SPHERE_DRAG_COEFFICIENT * area));
}

export function createRigidBodyState(): RigidBodyState {
  return {
    posX: 0,
    posY: 0,
    posZ: 0,
    velX: 0,
    velY: 0,
    velZ: 0,
    prevX: 0,
    prevY: 0,
    prevZ: 0,
    onGround: false,
  };
}

/** Place the body at rest at (x, y, z). */
export function resetRigidBody(state: RigidBodyState, xM: number, yM: number, zM: number): void {
  state.posX = state.prevX = xM;
  state.posY = state.prevY = yM;
  state.posZ = state.prevZ = zM;
  state.velX = state.velY = state.velZ = 0;
  state.onGround = false;
}

function effectiveDensity(p: CowBodyParams): number {
  return p.mode === 'vacuum' ? 0 : p.airDensity;
}

/** Drag force in N for body velocity (vx, vy, vz) in the given wind. Written to `out`. */
function dragAt(
  p: CowBodyParams,
  vx: number,
  vy: number,
  vz: number,
  wind: WindSample,
  out: WindSample,
): WindSample {
  if (p.mode === 'realistic') {
    throw new Error('Realistic mode is simulated by realisticCow.ts, not rigidBodyDrag.ts');
  }
  const rho = effectiveDensity(p);
  const rx = wind.x - vx;
  const ry = wind.y - vy;
  const rz = wind.z - vz;
  const speed = Math.sqrt(rx * rx + ry * ry + rz * rz);
  const k = 0.5 * rho * SPHERE_DRAG_COEFFICIENT * Math.PI * p.radiusM * p.radiusM * speed;
  out.x = k * rx;
  out.y = k * ry;
  out.z = k * rz;
  return out;
}

/** Drag force on the body, written into `out` (N). */
export function dragForce(
  p: CowBodyParams,
  state: RigidBodyState,
  wind: WindSample,
  out: WindSample,
): WindSample {
  return dragAt(p, state.velX, state.velY, state.velZ, wind, out);
}

// Module scratch. One body per step call, single-threaded, so sharing is safe.
const windScratch: WindSample = { x: 0, y: 0, z: 0 };
const forceScratch: WindSample = { x: 0, y: 0, z: 0 };
const a1: WindSample = { x: 0, y: 0, z: 0 };
const a2: WindSample = { x: 0, y: 0, z: 0 };

/** Acceleration at a trial state, written to `out`. */
function accel(
  p: CowBodyParams,
  field: WindField,
  grounded: boolean,
  px: number,
  py: number,
  pz: number,
  vx: number,
  vy: number,
  vz: number,
  tS: number,
  out: WindSample,
): WindSample {
  field.sample(px, py, pz, tS, windScratch);
  const f = dragAt(p, vx, vy, vz, windScratch, forceScratch);
  if (grounded) {
    const mEff = ROLLING_MASS_FACTOR * p.massKg;
    out.x = f.x / mEff;
    out.z = f.z / mEff;
    // The ground supplies whatever normal force is needed; only net lift leaves it.
    const ay = f.y / p.massKg - STANDARD_GRAVITY_MPS2;
    out.y = ay > 0 ? ay : 0;
  } else {
    out.x = f.x / p.massKg;
    out.y = f.y / p.massKg - STANDARD_GRAVITY_MPS2;
    out.z = f.z / p.massKg;
  }
  return out;
}

/**
 * Advance the body by dtS under gravity plus drag, explicit RK2 (midpoint),
 * split into substeps of at most MAX_INTERNAL_DT_S. Allocation-free.
 */
export function stepRigidBody(
  p: CowBodyParams,
  state: RigidBodyState,
  field: WindField,
  tS: number,
  dtS: number,
): void {
  state.prevX = state.posX;
  state.prevY = state.posY;
  state.prevZ = state.posZ;

  const n = Math.max(1, Math.ceil(dtS / MAX_INTERNAL_DT_S));
  const h = dtS / n;
  const half = 0.5 * h;
  let t = tS;

  for (let i = 0; i < n; i++) {
    const { posX, posY, posZ, velX, velY, velZ, onGround } = state;
    accel(p, field, onGround, posX, posY, posZ, velX, velY, velZ, t, a1);

    const mvx = velX + half * a1.x;
    const mvy = velY + half * a1.y;
    const mvz = velZ + half * a1.z;
    accel(
      p,
      field,
      onGround,
      posX + half * velX,
      posY + half * velY,
      posZ + half * velZ,
      mvx,
      mvy,
      mvz,
      t + half,
      a2,
    );

    state.posX = posX + h * mvx;
    state.posY = posY + h * mvy;
    state.posZ = posZ + h * mvz;
    state.velX = velX + h * a2.x;
    state.velY = velY + h * a2.y;
    state.velZ = velZ + h * a2.z;

    // Ground plane at y = 0; the sphere's center rests at y = r.
    if (state.posY <= p.radiusM) {
      state.posY = p.radiusM;
      if (state.velY < 0) state.velY = 0;
      state.onGround = true;
    } else {
      state.onGround = false;
    }
    t += h;
  }
}
